// Pending updates to protos files
import { FipsCrypto } from "pvcryptojs";
import { Backup as BackupPB, UserKey } from "src/common/keys/protos/backup_pb";
import { Helpers } from "src/common";

export interface BackupDataObject {
  user_id: string;
  backup_id: string;
  user_key: string;
  backup_key: string;
  backup_signing_key?: string;
}

export class BackupData {
  constructor(
    public user_id: string,
    public backup_id: string,
    public user_key: UserKey,
    public backup_key: UserKey
  ) {
  }

  public static deserialize(blob: Uint8Array): BackupPB.Data {
    return BackupPB.Data.deserializeBinary(blob);
  }

  // Description: Returns decrypted BackupPB.Data from password as Uint8Array
  public static async decrypt(passphrase: string, serialized_data: Uint8Array): Promise<Uint8Array> {
    return await FipsCrypto.decryptWithPassphrase(passphrase, serialized_data);
  }

  public get buffer(): BackupPB.Data {
    const pb_inst = new BackupPB.Data();
    pb_inst.setUserId(this.user_id);
    pb_inst.setBackupId(this.backup_id);
    pb_inst.setBackupKey(this.backup_key);
    pb_inst.setUserKey(this.user_key);
    return pb_inst;
  }

  public serialize(): Uint8Array {
    return this.buffer.serializeBinary();
  }

  public async encrypt(passphrase: string, serialized_data: Uint8Array): Promise<Uint8Array> {
    return await FipsCrypto.encryptWithPassphrase(passphrase, serialized_data);
  }

  // Description: Returns decoded text in the form of a backup proto object
  public static getDecodedText(backup_code: string): BackupPB {
    const decoded_text = Helpers.b64Decode(backup_code);
    return BackupPB.deserializeBinary(decoded_text);
  }

  // Description: decrypts the backup code and return a BackupDataObject
  public static getBackupDataObject(backup_data: BackupPB.Data): BackupDataObject {
    const user_id = backup_data.getUserId();
    const backup_id = backup_data.getBackupId();
    const backup_key = backup_data.getBackupKey();
    const user_key = backup_data.getUserKey();
    const backup_signing_key = backup_key?.getSigningKey();
    if (!!backup_key && !!user_key && !!user_id && !!backup_id && !!backup_signing_key) {
      return {
        user_id,
        backup_id,
        user_key: Helpers.b64Encode(user_key.serializeBinary()),
        backup_key: Helpers.b64Encode(backup_key.serializeBinary()),
        backup_signing_key: Helpers.b64Encode(backup_signing_key.serializeBinary())
      };
    } else {
      throw new Error("Failed to get Backup object!");
    }
  }
}

export class Backup {
  constructor(
    public encrypted_data: Uint8Array,
    public protocol_version: number
  ) {
  }

  public get buffer(): BackupPB {
    const pb_inst = new BackupPB();
    pb_inst.setProtocolVersion(this.protocol_version);
    pb_inst.setData(this.encrypted_data);
    return pb_inst;
  }

  public serialize(): Uint8Array {
    return this.buffer.serializeBinary();
  }

  public deserialize(blob: Uint8Array): BackupPB {
    return BackupPB.deserializeBinary(blob);
  }
}
